import React, { useEffect, useMemo, useRef, useState } from 'react';
import CaretDownIcon from './icons/CaretDownIcon';
import WarningIcon from './icons/WarningIcon';
import styles from './FloatingLabel.module.css';

/**
 * @param {object} props
 * @param {React.ReactElement=} props.buttonComponent
 * @param {React.ReactNode} props.children
 * @param {string=} props.className
 * @param {boolean=} props.dark
 * @param {string=} props.error
 */
export default function FloatingLabel({
  buttonComponent,
  children,
  className = '',
  dark = false,
  error,
}) {
  const [isFilled, setIsFilled] = useState(false);

  if (!React.isValidElement(children)) {
    throw new Error('valid form element must be provided');
  }

  /** @type {React.MutableRefObject<HTMLDivElement|null>} successMessageRef */
  const errorMessageRef = useRef(null);
  useEffect(() => {
    if (error && errorMessageRef.current) {
      errorMessageRef.current.focus();
    }
  }, [error, errorMessageRef.current]);

  const wrapperClasses = useMemo(() => {
    const classes = [styles.wrapper];
    if (dark) classes.push(styles.dark);
    if (error) classes.push(styles.error);
    if (isFilled) classes.push(styles.filled);
    if (buttonComponent) classes.push(styles.withButton);
    classes.push(className);
    return classes.join(' ');
  }, [buttonComponent, className, dark, error, isFilled]);

  return (
    <div className={wrapperClasses}>
      {React.cloneElement(children, {
        onChange:
          /** @param {React.ChangeEvent<HTMLInputElement|HTMLSelectElement>} e */ e => {
            setIsFilled(e.target.value.length > 0);
            if (children.props.onChange) {
              children.props.onChange(e);
            }
          },
      })}
      <label htmlFor={children.props.id}>{children.props['aria-label']}</label>
      {children.type === 'select' && (
        <CaretDownIcon className={styles.selectCaret} />
      )}
      {children.type !== 'select' &&
        buttonComponent &&
        React.cloneElement(buttonComponent, {
          className: `${buttonComponent.props.className || ''} ${
            styles.floatingButton
          }`,
          disabled: !isFilled,
        })}
      {error && (
        <div
          className={styles.errorMessage}
          ref={errorMessageRef}
          tabIndex={-1}
        >
          <WarningIcon />
          {error}
        </div>
      )}
    </div>
  );
}
